import { PromiseWrapper } from "../../api-polyfill/src/utils/pw";
import { spawnParamsToStartOptions, safeInteropRegister } from "../../api-polyfill/src/common/windowFunctions";
export class NotificationService {
    constructor(glue, logger, router) {
        this.glue = glue;
        this.logger = logger;
        this.router = router;
        this.notifications = [];
        this.lastIssuedAt = {};
        this.currentLastIssued = 0;
        this.ready = new PromiseWrapper();
    }
    async updateLastIssued(source, timestamp) {
        let shouldUpdate = false;
        const updatedLastIssued = new Date(timestamp * 1000).getTime();
        if (source && updatedLastIssued > (this.lastIssuedAt[source] ?? 0)) {
            shouldUpdate = true;
            this.lastIssuedAt[source] = updatedLastIssued;
        }
        if (updatedLastIssued > this.currentLastIssued) {
            shouldUpdate = true;
            this.currentLastIssued = updatedLastIssued;
        }
        if (shouldUpdate) {
            await this.glue.prefs.setFor("IO.Global.NotificationsLastIssued", {
                currentLastIssued: updatedLastIssued,
                lastIssuedAt: this.lastIssuedAt
            });
        }
    }
    async startPersistence() {
        this.logger.debug("notifications - loading persisted notifications (see console for more data)");
        const prefs = await this.glue.prefs.get("IO.Global.PersistedNotifications");
        this.notifications = prefs?.data?.notifications ?? [];
        console.debug("notifications - notifications persisted", this.notifications);
        if (this.notifications.length && this.glue.notifications.import) {
            await this.glue.notifications.import(this.notifications.map((n) => {
                return {
                    ...n,
                    state: "Acknowledged"
                };
            }));
        }
        await this.glue.notifications.onRaised(async (glueNotification) => {
            if (this.notifications.some((n) => n.id === glueNotification.id)) {
                return;
            }
            this.logger.debug("notifications - received onRaised", glueNotification);
            if (glueNotification.data?.issuedAt) {
                const date = new Date(glueNotification.data?.issuedAt);
                glueNotification.timestamp = Math.round(date.getTime() / 1000);
            }
            if (glueNotification.timestamp)
                this.updateLastIssued(glueNotification.source ?? glueNotification.appTitle, glueNotification.timestamp);
            this.notifications.push(glueNotification);
            const now = new Date();
            this.notifications = this.notifications.flatMap((notification) => {
                const then = notification.timestamp ? new Date(notification.timestamp * 1000) : new Date();
                const tooOld = now.getTime() - then.getTime() > 7 * 24 * 60 * 60 * 1000;
                return tooOld ? [] : [notification];
            });
            while (this.notifications.length > 1000) {
                this.notifications.shift();
            }
            await this.glue.prefs.setFor("IO.Global.PersistedNotifications", { notifications: this.notifications });
            this.logger.debug("notifications - notifications persisted onRaised (see console for more data)");
            console.debug("notifications - notifications persisted onRaised", this.notifications);
        });
        await this.glue.notifications.onClosed(async (notification) => {
            this.logger.debug("notifications - received onClosed (see console for full state)", notification);
            this.notifications.forEach((n) => {
                if (n.id === notification.id) {
                    n.state = "Closed";
                }
            });
            await this.glue.prefs.setFor("IO.Global.PersistedNotifications", { notifications: this.notifications });
            console.debug("notifications - notifications persisted onClosed", this.notifications);
        });
        await this.glue.notifications.onStateChanged(async (notification, state) => {
            this.logger.debug("notifications - received onStateChanged (see console for full state)", notification, state);
            this.notifications.forEach((n) => {
                if (n.id === notification.id) {
                    n.state = state;
                }
            });
            await this.glue.prefs.setFor("IO.Global.PersistedNotifications", { notifications: this.notifications });
            console.debug("notifications - notifications persisted onStateChanged", this.notifications);
        });
    }
    async getLastIssuedAt(param) {
        if (param?.source) {
            return { lastIssuedAt: new Date(this.lastIssuedAt[param.source] ?? 0).toISOString() };
        }
        else {
            return { lastIssuedAt: new Date(this.currentLastIssued).toISOString() };
        }
    }
    async performAction(params) {
        this.logger.debug("notifications - NotificationService.performAction", params);
        const { id, milliseconds, channel, payload, component, spawnParams } = params;
        switch (params.type) {
            case "TRANSMIT":
                this.router.transmit(channel, payload);
                break;
            case "PUBLISH":
                this.router.publish(channel, payload);
                break;
            case "QUERY":
                this.router.query(channel, payload);
                break;
            case "SPAWN":
                const application = this.glue.appManager.application(component);
                const applicationOptions = await spawnParamsToStartOptions(this.glue, spawnParams, {});
                try {
                    await application?.start(undefined, applicationOptions);
                }
                catch (e) {
                    this.logger.error(e);
                }
                break;
            case "SNOOZE":
                await this.glue.notifications.snooze(id, milliseconds ?? 30000);
                break;
            case "DISMISS":
                await this.glue.notifications.clear(id);
                break;
        }
    }
    async registerHandlers() {
        this.logger.debug("notifications - FSBL.NotificationClient registering handlers");
        const previouslyRegisteredHandlers = this.glue.interop.methods({ name: "HandleNotificationActionTypeSPAWN" });
        if (previouslyRegisteredHandlers.length > 0) {
            this.logger.error("notifications - Error: Went to register glue handlers for the NotificationService, but they've already been registered.");
        }
        await this.glue.interop.register("FSBL.Clients.NotificationClient.performAction", safeInteropRegister((params) => this.performAction(params), this.logger));
        await this.glue.interop.register("FSBL.Clients.NotificationClient.getLastIssuedAt", safeInteropRegister((source) => this.getLastIssuedAt(source), this.logger));
        await this.router.subscribe("notification-ui", (err, response) => {
            const { showCenter } = response.data ?? {};
            if (showCenter) {
                this.glue.notifications.panel.show();
            }
            else {
                this.glue.notifications.panel.hide();
            }
        });
        this.logger.debug("notifications - FSBL.NotificationClient finished initializing");
        this.ready.resolve();
    }
    async initialize() {
        const pref = await this.glue.prefs.get("IO.Global.NotificationsLastIssued");
        if (pref?.data) {
            this.currentLastIssued = pref.data.currentLastIssued ?? 0;
            this.lastIssuedAt = pref.data.lastIssuedAt;
        }
        await this.startPersistence();
    }
    async start() {
        await this.router.onReady();
        await this.registerHandlers();
    }
}
